import React, { memo, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { getAllCategories } from "../Redux/Slice/category.slice";
import { useNavigate } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";

const InfiniteMarqueeCards = memo(function InfiniteMarqueeCards() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categories, loading } = useSelector(
    (s) => ({
      categories: s.category.categories,
      loading: s.category.loading,
    }),
    shallowEqual
  );

    useEffect(() => {
      if (!Array.isArray(categories) || categories.length === 0) {
        dispatch(getAllCategories());
      }
    }, [dispatch, categories.length]);

  // Marquee pause/resume
  const marqueeRef = useRef(null);
  const handleCardMouseEnter = useCallback(() => {
    if (marqueeRef.current)
      marqueeRef.current.style.animationPlayState = "paused";
  }, []);
  const handleCardMouseLeave = useCallback(() => {
    if (marqueeRef.current)
      marqueeRef.current.style.animationPlayState = "running";
  }, []);

  const Card = memo(function Card({ item, onMouseEnter, onMouseLeave }) {
    const ref = useRef(null);

    const onMove = useCallback((e) => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      const rx = (y / r.height - 0.5) * -12;
      const ry = (x / r.width - 0.5) * 12;
      el.style.setProperty("--rx", `${rx.toFixed(2)}deg`);
      el.style.setProperty("--ry", `${ry.toFixed(2)}deg`);
    }, []);

    const onLeave3d = useCallback(() => {
      const el = ref.current;
      if (!el) return;
      el.style.setProperty("--rx", `0deg`);
      el.style.setProperty("--ry", `0deg`);
    }, []);

    const bgUrl = item?.category_image?.url;

    return (
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={(e) => {
          onMouseLeave && onMouseLeave(e);
          onLeave3d(e);
        }}
        onMouseEnter={onMouseEnter}
        onClick={() => {
          navigate("/games", {
            state: {
              name: item?.categoryName,
              _id: item?._id,
              id: item?.categoryName.toLowerCase(),
            },
          });
        }}
        className="card-3d relative flex-shrink-0 w-[180px] h-[250px]  md:w-[230px] md:h-[350px] mx-5 rounded-xl overflow-hidden"
        style={{
          backgroundImage: bgUrl ? `url(${bgUrl})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute bottom-0 left-0 w-full flex items-end h-[30%] bg-gradient-to-t from-black to-transparent">
          <div className="z-10 text-white text-center w-full mb-4">
            <h3
              className="text-2xl font-bold line-clamp-1"
              style={{
                perspective: "6000px",
                transform: "translateZ(100px)",
                willChange: "transform",
              }}
            >
              {item?.categoryName || "Category"}
            </h3>
          </div>
        </div>
      </div>
    );
  });

  return (
    <div className="relative w-full overflow-hidden py-14 lg:py-28">
      {/* Soft colored glows for gaming feel */}
      <div className="rounded-md md:rounded-3xl p-[1px] w-[90%] md:w-[80%] mx-auto overflow-hidden shadow-[0px_0px_2px_0px] shadow-primary-light">
        <div className="rotating-border w-full rounded-3xl">
          <div className="relative z-10 py-5 w-full mx-auto rounded-md md:rounded-3xl backdrop-blur-lg bg-black">
            {/* Header */}
            <div className="flex items-center  justify-between mb-5 w-full  px-4 sm:px-8 ">
              <span className="inline-block rounded-md px-2  md:px-4 py-1.5 shadow-lg">
                <motion.span
                  className="text-white font-extrabold md:text-3xl tracking-wide"
                  initial={{ opacity: 0, letterSpacing: "0.05em" }}
                  animate={{ opacity: 1, letterSpacing: "0.2em" }}
                  transition={{ duration: 1, ease: "easeInOut" }}
                >
                  Categories
                </motion.span>
              </span>
              <button
                type="button"
                onClick={() => {
                  navigate("/games");
                }}
                className="px-2 py-1 md:px-5 md:py-2 text-sm font-normal rounded-xl text-white md:font-semibold bg-gradient-primary transition-colors shadow-md"
              >
                View More
              </button>
            </div>

            {/* Content */}
            <div className="relative w-full overflow-hidden py-5 ">
              {/* <div
                className="flex animate-marquee-left w-full"
                ref={marqueeRef}
              >
                {[...Array(4)].map((_, setIndex) =>
                  categories.map((item, idx) => (
                    <Card
                    key={`set-${setIndex}-cat-${item?._id || idx}`}
                    item={item || {}}
                    onMouseEnter={handleCardMouseEnter}
                    onMouseLeave={handleCardMouseLeave}
                  />
                  ))
                )}
              </div> */}
              <div className="marquee">
                <div className="marquee__inner" ref={marqueeRef}>
                  {categories.map((item, idx) => (
                    <Card
                      key={`cat-${item?._id || idx}`}
                      item={item || {}}
                      onMouseEnter={handleCardMouseEnter}
                      onMouseLeave={handleCardMouseLeave}
                    />
                  ))}
                  {categories.map((item, idx) => (
                    <Card
                      key={`cat-duplicate-${item?._id || idx}`}
                      item={item || {}}
                      onMouseEnter={handleCardMouseEnter}
                      onMouseLeave={handleCardMouseLeave}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center mt-6 text-sm text-gray-400">Loading...</div>
      )}
    </div>
  );
});

export default InfiniteMarqueeCards;
